import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  Account,
  FormPayload,
  FormPayloadItem,
  FormStatuses,
  PaymentFormSchema,
  ValidationStatuses,
} from '@cashier/cashier-model';

export interface PaymentFormState {
  status: FormStatuses;
  version: string;
  formPayload: FormPayload;
  formSchema: PaymentFormSchema | null;
  options: { label: string; value: string }[];
}

export const PAYMENT_FORM_FEATURE_KEY = 'paymentForm';

export const initialPaymentFormState: PaymentFormState = {
  status: FormStatuses.INITIATED,
  version: '',
  formPayload: {},
  formSchema: null,
  options: [],
};

export const paymentFormSlice = createSlice({
  name: PAYMENT_FORM_FEATURE_KEY,
  initialState: initialPaymentFormState,
  reducers: {
    initiatePaymentForm: (
      _,
      __: PayloadAction<{
        account: Account | null;
        amount?: string | null;
      }>,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
    ) => {},
    fillPaymentForm: (
      state,
      action: PayloadAction<{
        version: string;
        schema: PaymentFormSchema;
        payload: FormPayload;
      }>,
    ) => {
      state.version = action.payload.version;
      state.formSchema = action.payload.schema;
      state.formPayload = action.payload.payload;
    },
    updatePaymentFormPayloadItem: (
      state,
      action: PayloadAction<{
        name: string;
        fieldPayload: FormPayloadItem;
      }>,
    ) => {
      const { name, fieldPayload: newPayload } = action.payload;

      const oldPayload = state.formPayload[name] || {};

      state.formPayload[name] = {
        ...oldPayload,
        ...newPayload,
      };
    },
    enrichPayloadWithErrors: (
      state,
      action: PayloadAction<Record<string, string[]>>,
    ) => {
      for (const [fieldName, errors] of Object.entries(action.payload)) {
        if (state.formPayload[fieldName]) {
          state.formPayload[fieldName] = {
            ...state.formPayload[fieldName],
            errors,
            validationStatus: ValidationStatuses.FAILED,
            interactionCompleted: true,
          };
        }
      }
    },
    setPaymentFormStatus: (state, action: PayloadAction<FormStatuses>) => {
      state.status = action.payload;
    },
    fetchDirecta24OptionsStart: (
      _,
      __: PayloadAction<{
        url: string;
        parameters: Record<string, string>;
      }>,
    ) => undefined,
    fetchMifinityOptionsStart: (_, __: PayloadAction<{ url: string }>) =>
      undefined,
    fetchMifinityOptionsSuccess: (state, action: PayloadAction<any>) => {
      state.options = action.payload.map(
        ({ payerName }: { payerName: string }) => ({
          value: payerName,
          label: payerName,
        }),
      );
    },
    fetchDirecta24OptionsSuccess: (state, action: PayloadAction<any>) => {
      if (!action.payload.code) {
        state.options = action.payload.map(
          ({ code, name }: { code: string; name: string }) => ({
            value: code,
            label: name,
          }),
        );
      }
    },
    fetchDirecta24OptionsFailure: (_, __: PayloadAction<any>) => undefined,
  },
});

export const paymentFormReducer = paymentFormSlice.reducer;

export const {
  enrichPayloadWithErrors,
  updatePaymentFormPayloadItem,
  setPaymentFormStatus,
  initiatePaymentForm,
  fillPaymentForm,
  fetchDirecta24OptionsStart,
  fetchDirecta24OptionsSuccess,
  fetchDirecta24OptionsFailure,
  fetchMifinityOptionsStart,
  fetchMifinityOptionsSuccess,
} = paymentFormSlice.actions;
